import React from 'react';
import { Award, Target, Shield, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';
import Seo from '../components/Seo';

const About = () => {
  const values = [
    {
      icon: Award,
      title: "Excellence",
      description: "Delivering the highest quality surveying services with unwavering precision."
    },
    {
      icon: Target,
      title: "Accuracy",
      description: "State-of-the-art equipment ensuring accurate results every time."
    },
    {
      icon: Shield,
      title: "Integrity",
      description: "Building trust through transparent practices and honest communication."
    },
    {
      icon: TrendingUp,
      title: "Innovation",
      description: "Continuously adopting modern surveying technologies for efficiency."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="About Us | Versatile Geopick NIG Ltd"
        description="Licensed land surveying firm in Lagos (RC-2001517) built on precision, integrity and reliability, serving property buyers and owners across Nigeria."
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-50 pt-24">
        <div className="absolute inset-0 z-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1766830110938-0ea8a6d78ecb"
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-50/60 to-white"></div>
        </div>
        
        <Reveal className="relative z-10 text-center px-6" duration={0.9}>
          <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">About Us</p>
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 tracking-tight">
            Built on Trust & <span className="font-semibold text-emerald-700">Precision</span>
          </h1>
        </Reveal>
      </section>

      {/* Company Overview */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <Reveal direction="left" className="space-y-6">
              <h2 className="text-4xl font-light text-stone-800 leading-tight">
                Professional Land Surveying<br />in Lagos & Nigeria
              </h2>
              <div className="space-y-4 text-lg text-stone-600 leading-relaxed">
                <p>
                  <strong className="text-stone-800 font-medium">Versatile Geopick NIG Ltd</strong> is a licensed land surveying firm based in Lagos, serving clients across Nigeria.
                </p>
                <p>
                  Registered under <strong className="text-emerald-700">RC-2001517</strong>, we provide accurate, reliable, and legally compliant surveying services.
                </p>
                <p>
                  Our certified surveyors bring years of expertise in cadastral surveys, topographic mapping, boundary identification, and comprehensive land verification.
                </p>
              </div>
              <div className="pt-4">
                <div className="inline-flex items-center gap-3 px-5 py-3 bg-emerald-50 rounded-2xl border border-emerald-200">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
                  <span className="text-stone-700 font-medium">Licensed & Certified Surveyors</span>
                </div>
              </div>
            </Reveal>
            <Reveal direction="right" className="relative">
              <img 
                src="https://images.unsplash.com/photo-1770822662903-aa69f6aa9b03"
                alt="Professional Surveying"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <div className="max-w-6xl mx-auto px-6">
          <RevealGroup className="grid md:grid-cols-2 gap-8" stagger={0.15}>
            <RevealItem direction="left">
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-12">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="h-7 w-7 text-emerald-700" />
                </div>
                <h3 className="text-3xl font-light text-stone-800 mb-6">Our Mission</h3>
                <p className="text-lg text-stone-600 leading-relaxed">
                  To provide accurate, reliable, and professional land surveying services that empower our clients to make informed property decisions with full compliance to Nigerian land regulations.
                </p>
              </CardContent>
            </Card>
            </RevealItem>

            <RevealItem direction="right">
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-12">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                  <TrendingUp className="h-7 w-7 text-emerald-700" />
                </div>
                <h3 className="text-3xl font-light text-stone-800 mb-6">Our Vision</h3>
                <p className="text-lg text-stone-600 leading-relaxed">
                  To be Nigeria's most trusted land surveying firm, known for our commitment to accuracy, professionalism, and innovation in protecting property interests.
                </p>
              </CardContent>
            </Card>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-20">
            <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">Our Values</p>
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 tracking-tight">
              Principles That Guide Us
            </h2>
          </Reveal>

          <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <RevealItem key={index}>
                <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon className="h-7 w-7 text-emerald-700" />
                    </div>
                    <h3 className="text-xl font-medium text-stone-800 mb-3">{value.title}</h3>
                    <p className="text-stone-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-24 bg-emerald-700">
        <Reveal className="max-w-4xl mx-auto px-6 text-center" direction="scale">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8 leading-tight">
            Committed to Your Property Success
          </h2>
          <p className="text-xl text-emerald-100 leading-relaxed">
            We understand that land represents significant investment. That's why we commit to providing accurate, timely, and legally compliant surveying services for confident property transactions.
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
